import { useNavigate } from 'react-router-dom';

import { AppIcon } from '@backrs_ui/common-ui';

import { ReactComponent as BackrsFilledDropShadow } from '@backrs_ui/common-ui/src/assets/images/backrs-filled-drop-shadow.svg';
import { LoginCarousel } from '@backrs_ui/common-ui/src/molecules/Login/LoginCarousel';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Typography, Theme, SxProps, Link } from '@mui/material';

import { SignUpForm, SignUpFormData } from './SignUpForm';

export type { SignUpFormData } from './SignUpForm';

const STRINGS = appStrings.modules.signup;

type SignUpPageProps = Readonly<{
  onSignUpComplete: (data: SignUpFormData) => void;
  initialEmail?: string | null;
  isFamilyInvite?: boolean;
}>;

export function SignUpPage({
  onSignUpComplete,
  initialEmail = null,
  isFamilyInvite = false,
}: SignUpPageProps) {
  const navigate = useNavigate();

  return (
    <Box sx={componentContainerSx}>
      <Box sx={containerStyle}>
        <Box sx={signUpFormStyle}>
          <Box sx={responsiveBackrsLogoStyle}>
            <BackrsFilledDropShadow width="48px" height="48px" />
          </Box>
          <Box sx={textContainerSx}>
            <Typography
              variant="display-xs-semibold"
              color={(theme) => theme.backrs.colors['Colors/Text/text-primary']}
            >
              {STRINGS.title}
            </Typography>
            <Typography
              variant="text-md-regular"
              color={(theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
              sx={{ textAlign: 'center' }}
            >
              {STRINGS.subtitle}
            </Typography>
          </Box>
          <Box sx={signUpFormWrapperSx}>
            <SignUpForm
              onSignUpComplete={onSignUpComplete}
              onLoginClick={() => navigate(APP_ROUTE.LOGIN)}
              initialEmail={initialEmail}
              isFamilyInvite={isFamilyInvite}
            />
          </Box>
        </Box>
        <Box sx={footerSx}>
          <Typography variant="text-sm-regular" sx={footerTextSx}>
            {STRINGS.copyright}
          </Typography>
          <Link href={`mailto:${STRINGS.supportEmail}`} sx={supportEmailLinkSx}>
            <AppIcon iconName="Mail01" diameter="16px" color="Colors/Text/text-tertiary" />
            <Typography variant="text-sm-regular" sx={footerTextSx}>
              {STRINGS.supportEmail}
            </Typography>
          </Link>
        </Box>
      </Box>

      <Box sx={carouselContainerSx}>
        <LoginCarousel />
      </Box>
    </Box>
  );
}

const componentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: '1fr 1fr',
  },
  color: backrs.colors['Yellow/950'],
  height: '100dvh',
  overflow: 'hidden',
});

const carouselContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: {
    xs: 'none',
    md: 'block',
  },
  backgroundColor: backrs.colors['Colors/Foreground/fg-brand-secondary'],
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
  width: '100%',
  height: '100dvh',
  borderLeft: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const containerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  margin: '0 auto',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: {
    xs: 'flex-start',
    md: 'center',
  },
  alignSelf: 'center',
  justifySelf: 'center',
  backgroundColor: backrs.colors['Beige/50'],
  height: '100dvh',
  width: '100%',
});

const signUpFormStyle: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: {
    xs: backrs.colors['Beige/50'],
    sm: backrs.colors['Colors/Base/White'],
  },
  py: backrs.spacing['spacing-2xl'],
  px: backrs.spacing['spacing-5xl'],
  borderRadius: {
    xs: 0,
    sm: backrs.radius['radius-md'],
  },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: {
    xs: '100%',
    sm: '440px',
  },
  width: '100%',
  boxSizing: 'border-box',
  border: {
    xs: 'none',
    sm: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  },
  margin: {
    xs: 0,
    sm: `auto ${backrs.spacing['spacing-4xl']}`,
    lg: `auto ${backrs.spacing['spacing-6xl']}`,
  },
  gap: backrs.spacing['spacing-3xl'],
});

const signUpFormWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  paddingBottom: backrs.spacing['spacing-xl'],
});

const responsiveBackrsLogoStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  maxWidth: '48px',
  maxHeight: '48px',
  mt: backrs.spacing['spacing-3xl'],
});

const textContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'center',
  gap: backrs.spacing['spacing-md'],
  mb: `-${backrs.spacing['spacing-2xl']}`,
});

const footerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  width: '100%',
  px: backrs.spacing['spacing-4xl'],
  pt: backrs.spacing['spacing-2xl'],
});

const footerTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const supportEmailLinkSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
});
